// src/store/slices/karigar/karigarSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchKarigars,
  fetchKarigarById,
  createKarigar,
  updateKarigar,
  updateKarigarStatus,
  deleteKarigar,
  fetchKarigarsByBranch,
  fetchKarigarAvailability,
  fetchBulkKarigarAvailability,
  fetchKarigarStats,
  addKarigarToBranch,
  removeKarigarFromBranch,
  updateKarigarSkills,
  verifyKarigarSkill,
  updateWorkPreferences,
  searchKarigars,
  fetchAvailableKarigars,
  fetchPerformanceReport,
  approveMultipleKarigars,
  suspendMultipleKarigars,
  deleteMultipleKarigars,
  setKarigarPassword,
  resetKarigarPassword,
  checkKarigarPassword,
  verifyBankDetails,
  fetchEstimatedHoursForProduct,
} from "../../thunks/karigar/karigarThunks";

const initialState = {
  // List data
  karigars: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },

  // Current item
  currentKarigar: null,

  // Filters
  filters: {
    status: "",
    branchCode: "",
    skill: "",
    search: "",
    isAvailable: "",
  },

  // Single karigar availability
  availability: {
    calendar: [],
    summary: null,
    currentStatus: null,
    capacity: null,
    estimation: null,
    unavailablePeriods: [],
    availableWindows: [],
  },

  // ✅ FIXED: Bulk availability now stores full object structure
  bulkAvailability: {
    productKarigars: [],
    otherKarigars: [],
    allKarigars: [],
    recommendations: null,
    summary: null,
  },

  // Stats
  stats: null,

  // Available karigars for assignment
  availableKarigars: [],

  // Performance report
  performanceReport: null,

  // Password status
  passwordStatus: null,

  // Estimated hours result
  estimatedHours: null,

  // Loading states
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    status: false,
    availability: false,
    bulkAvailability: false,
    stats: false,
    branch: false,
    skills: false,
    workPreferences: false,
    search: false,
    available: false,
    performanceReport: false,
    bulkApprove: false,
    bulkSuspend: false,
    bulkDelete: false,
    password: false,
    verification: false,
    estimate: false,
  },

  // Messages
  error: null,
  successMessage: null,
};

// Helper to extract data from API response
const extractData = (payload) => {
  // Handle different response structures
  if (payload?.data) {
    return payload.data;
  }
  return payload;
};

const karigarSlice = createSlice({
  name: "karigar/karigar",
  initialState,
  reducers: {
    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Clear current
    clearCurrentKarigar: (state) => {
      state.currentKarigar = null;
    },
    clearAvailability: (state) => {
      state.availability = initialState.availability;
      state.bulkAvailability = initialState.bulkAvailability;
    },
    clearAvailableKarigars: (state) => {
      state.availableKarigars = [];
    },
    clearPerformanceReport: (state) => {
      state.performanceReport = null;
    },
    clearBulkAvailability: (state) => {
      state.bulkAvailability = initialState.bulkAvailability;
    },
    clearEstimatedHours: (state) => {
      state.estimatedHours = null;
    },

    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== FETCH KARIGARS ==========
      .addCase(fetchKarigars.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchKarigars.fulfilled, (state, action) => {
        state.loading.list = false;
        const data = extractData(action.payload);
        // Handle both { karigars, pagination } and direct array
        state.karigars = data.karigars || data || [];
        if (data.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(fetchKarigars.rejected, (state, action) => {
        state.loading.list = false;
        state.error = action.payload?.message || "Failed to fetch karigars";
        state.karigars = [];
      })

      // ========== FETCH KARIGAR BY ID ==========
      .addCase(fetchKarigarById.pending, (state) => {
        state.loading.detail = true;
        state.error = null;
      })
      .addCase(fetchKarigarById.fulfilled, (state, action) => {
        state.loading.detail = false;
        const data = extractData(action.payload);
        state.currentKarigar = data.karigar || data || null;
      })
      .addCase(fetchKarigarById.rejected, (state, action) => {
        state.loading.detail = false;
        state.error = action.payload?.message || "Failed to fetch karigar";
      })

      // ========== CREATE KARIGAR ==========
      .addCase(createKarigar.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createKarigar.fulfilled, (state, action) => {
        state.loading.create = false;
        const data = extractData(action.payload);
        const newKarigar = data.karigar || data;
        if (newKarigar && newKarigar._id) {
          state.karigars = [newKarigar, ...state.karigars];
          state.pagination.total += 1;
        }
        state.successMessage =
          action.payload?.message || "Karigar created successfully";
      })
      .addCase(createKarigar.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload?.message || "Failed to create karigar";
      })

      // ========== UPDATE KARIGAR ==========
      .addCase(updateKarigar.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateKarigar.fulfilled, (state, action) => {
        state.loading.update = false;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;
        if (updatedKarigar && updatedKarigar._id) {
          const index = state.karigars.findIndex(
            (k) => k._id === updatedKarigar._id
          );
          if (index !== -1) {
            state.karigars[index] = updatedKarigar;
          }
          if (state.currentKarigar?._id === updatedKarigar._id) {
            state.currentKarigar = updatedKarigar;
          }
        }
        state.successMessage =
          action.payload?.message || "Karigar updated successfully";
      })
      .addCase(updateKarigar.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload?.message || "Failed to update karigar";
      })

      // ========== UPDATE KARIGAR STATUS ==========
      .addCase(updateKarigarStatus.pending, (state) => {
        state.loading.status = true;
        state.error = null;
      })
      .addCase(updateKarigarStatus.fulfilled, (state, action) => {
        state.loading.status = false;
        const { karigarId, status } = action.payload;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;

        // Update in list
        const index = state.karigars.findIndex((k) => k._id === karigarId);
        if (index !== -1) {
          state.karigars[index] = updatedKarigar || {
            ...state.karigars[index],
            status,
          };
        }

        // Update current if same
        if (state.currentKarigar?._id === karigarId) {
          state.currentKarigar = updatedKarigar || {
            ...state.currentKarigar,
            status,
          };
        }

        state.successMessage =
          action.payload?.message || `Karigar status updated to ${status}`;
      })
      .addCase(updateKarigarStatus.rejected, (state, action) => {
        state.loading.status = false;
        state.error = action.payload?.message || "Failed to update status";
      })

      // ========== DELETE KARIGAR ==========
      .addCase(deleteKarigar.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteKarigar.fulfilled, (state, action) => {
        state.loading.delete = false;
        const { karigarId } = action.payload;
        state.karigars = state.karigars.filter((k) => k._id !== karigarId);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        if (state.currentKarigar?._id === karigarId) {
          state.currentKarigar = null;
        }
        state.successMessage = "Karigar deleted successfully";
      })
      .addCase(deleteKarigar.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload?.message || "Failed to delete karigar";
      })

      // ========== FETCH KARIGARS BY BRANCH ==========
      .addCase(fetchKarigarsByBranch.pending, (state) => {
        state.loading.branch = true;
        state.error = null;
      })
      .addCase(fetchKarigarsByBranch.fulfilled, (state, action) => {
        state.loading.branch = false;
        const data = extractData(action.payload);
        state.karigars = data.karigars || data || [];
        if (data.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(fetchKarigarsByBranch.rejected, (state, action) => {
        state.loading.branch = false;
        state.error =
          action.payload?.message || "Failed to fetch karigars by branch";
        state.karigars = [];
      })

      // ========== FETCH KARIGAR AVAILABILITY ==========
      .addCase(fetchKarigarAvailability.pending, (state) => {
        state.loading.availability = true;
        state.error = null;
      })
      .addCase(fetchKarigarAvailability.fulfilled, (state, action) => {
        state.loading.availability = false;
        const data = extractData(action.payload);
        state.availability = {
          calendar: data.calendar || [],
          summary: data.calendarSummary || data.summary || null,
          currentStatus: data.currentStatus || null,
          capacity: data.capacity || null,
          estimation: data.estimation || null,
          unavailablePeriods: data.unavailablePeriods || [],
          availableWindows: data.availableWindows || [],
        };
      })
      .addCase(fetchKarigarAvailability.rejected, (state, action) => {
        state.loading.availability = false;
        state.error = action.payload?.message || "Failed to fetch availability";
      })

      // ========== FETCH BULK KARIGAR AVAILABILITY ==========
      // ✅ FIXED: Now stores the full object structure from API
      .addCase(fetchBulkKarigarAvailability.pending, (state) => {
        state.loading.bulkAvailability = true;
        state.error = null;
      })
      .addCase(fetchBulkKarigarAvailability.fulfilled, (state, action) => {
        state.loading.bulkAvailability = false;
        const data = extractData(action.payload);

        // Store the full structure matching API response
        state.bulkAvailability = {
          productKarigars: data.productKarigars || [],
          otherKarigars: data.otherKarigars || [],
          allKarigars: data.allKarigars || [],
          recommendations: data.recommendations || null,
          summary: data.summary || null,
        };
      })
      .addCase(fetchBulkKarigarAvailability.rejected, (state, action) => {
        state.loading.bulkAvailability = false;
        state.error =
          action.payload?.message || "Failed to fetch bulk availability";
        state.bulkAvailability = initialState.bulkAvailability;
      })

      // ========== FETCH KARIGAR STATS ==========
      .addCase(fetchKarigarStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchKarigarStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        const data = extractData(action.payload);
        state.stats = data;
      })
      .addCase(fetchKarigarStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload?.message || "Failed to fetch stats";
      })

      // ========== ADD KARIGAR TO BRANCH ==========
      .addCase(addKarigarToBranch.pending, (state) => {
        state.loading.branch = true;
        state.error = null;
      })
      .addCase(addKarigarToBranch.fulfilled, (state, action) => {
        state.loading.branch = false;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;
        if (updatedKarigar && updatedKarigar._id) {
          const index = state.karigars.findIndex(
            (k) => k._id === updatedKarigar._id
          );
          if (index !== -1) {
            state.karigars[index] = updatedKarigar;
          }
          if (state.currentKarigar?._id === updatedKarigar._id) {
            state.currentKarigar = updatedKarigar;
          }
        }
        state.successMessage =
          action.payload?.message || "Karigar added to branch";
      })
      .addCase(addKarigarToBranch.rejected, (state, action) => {
        state.loading.branch = false;
        state.error = action.payload?.message || "Failed to add to branch";
      })

      // ========== REMOVE KARIGAR FROM BRANCH ==========
      .addCase(removeKarigarFromBranch.pending, (state) => {
        state.loading.branch = true;
        state.error = null;
      })
      .addCase(removeKarigarFromBranch.fulfilled, (state, action) => {
        state.loading.branch = false;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;
        if (updatedKarigar && updatedKarigar._id) {
          const index = state.karigars.findIndex(
            (k) => k._id === updatedKarigar._id
          );
          if (index !== -1) {
            state.karigars[index] = updatedKarigar;
          }
          if (state.currentKarigar?._id === updatedKarigar._id) {
            state.currentKarigar = updatedKarigar;
          }
        }
        state.successMessage =
          action.payload?.message || "Karigar removed from branch";
      })
      .addCase(removeKarigarFromBranch.rejected, (state, action) => {
        state.loading.branch = false;
        state.error = action.payload?.message || "Failed to remove from branch";
      })

      // ========== UPDATE KARIGAR SKILLS ==========
      .addCase(updateKarigarSkills.pending, (state) => {
        state.loading.skills = true;
        state.error = null;
      })
      .addCase(updateKarigarSkills.fulfilled, (state, action) => {
        state.loading.skills = false;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;
        if (updatedKarigar && updatedKarigar._id) {
          const index = state.karigars.findIndex(
            (k) => k._id === updatedKarigar._id
          );
          if (index !== -1) {
            state.karigars[index] = updatedKarigar;
          }
          if (state.currentKarigar?._id === updatedKarigar._id) {
            state.currentKarigar = updatedKarigar;
          }
        }
        state.successMessage = action.payload?.message || "Skills updated";
      })
      .addCase(updateKarigarSkills.rejected, (state, action) => {
        state.loading.skills = false;
        state.error = action.payload?.message || "Failed to update skills";
      })

      // ========== VERIFY KARIGAR SKILL ==========
      .addCase(verifyKarigarSkill.pending, (state) => {
        state.loading.skills = true;
        state.error = null;
      })
      .addCase(verifyKarigarSkill.fulfilled, (state, action) => {
        state.loading.skills = false;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;
        if (updatedKarigar && updatedKarigar._id) {
          const index = state.karigars.findIndex(
            (k) => k._id === updatedKarigar._id
          );
          if (index !== -1) {
            state.karigars[index] = updatedKarigar;
          }
          if (state.currentKarigar?._id === updatedKarigar._id) {
            state.currentKarigar = updatedKarigar;
          }
        }
        state.successMessage = action.payload?.message || "Skill verified";
      })
      .addCase(verifyKarigarSkill.rejected, (state, action) => {
        state.loading.skills = false;
        state.error = action.payload?.message || "Failed to verify skill";
      })

      // ========== UPDATE WORK PREFERENCES ==========
      .addCase(updateWorkPreferences.pending, (state) => {
        state.loading.workPreferences = true;
        state.error = null;
      })
      .addCase(updateWorkPreferences.fulfilled, (state, action) => {
        state.loading.workPreferences = false;
        const data = extractData(action.payload);
        const updatedKarigar = data.karigar || data;
        if (updatedKarigar && updatedKarigar._id) {
          const index = state.karigars.findIndex(
            (k) => k._id === updatedKarigar._id
          );
          if (index !== -1) {
            state.karigars[index] = updatedKarigar;
          }
          if (state.currentKarigar?._id === updatedKarigar._id) {
            state.currentKarigar = updatedKarigar;
          }
        }
        state.successMessage =
          action.payload?.message || "Work preferences updated";
      })
      .addCase(updateWorkPreferences.rejected, (state, action) => {
        state.loading.workPreferences = false;
        state.error =
          action.payload?.message || "Failed to update work preferences";
      })

      // ========== SEARCH KARIGARS ==========
      .addCase(searchKarigars.pending, (state) => {
        state.loading.search = true;
        state.error = null;
      })
      .addCase(searchKarigars.fulfilled, (state, action) => {
        state.loading.search = false;
        const data = extractData(action.payload);
        state.karigars = data.karigars || data || [];
        if (data.pagination) {
          state.pagination = data.pagination;
        }
      })
      .addCase(searchKarigars.rejected, (state, action) => {
        state.loading.search = false;
        state.error = action.payload?.message || "Search failed";
        state.karigars = [];
      })

      // ========== FETCH AVAILABLE KARIGARS ==========
      .addCase(fetchAvailableKarigars.pending, (state) => {
        state.loading.available = true;
        state.error = null;
      })
      .addCase(fetchAvailableKarigars.fulfilled, (state, action) => {
        state.loading.available = false;
        const data = extractData(action.payload);
        state.availableKarigars = data.karigars || data || [];
      })
      .addCase(fetchAvailableKarigars.rejected, (state, action) => {
        state.loading.available = false;
        state.error =
          action.payload?.message || "Failed to fetch available karigars";
        state.availableKarigars = [];
      })

      // ========== FETCH PERFORMANCE REPORT ==========
      .addCase(fetchPerformanceReport.pending, (state) => {
        state.loading.performanceReport = true;
        state.error = null;
      })
      .addCase(fetchPerformanceReport.fulfilled, (state, action) => {
        state.loading.performanceReport = false;
        const data = extractData(action.payload);
        state.performanceReport = data;
      })
      .addCase(fetchPerformanceReport.rejected, (state, action) => {
        state.loading.performanceReport = false;
        state.error =
          action.payload?.message || "Failed to fetch performance report";
      })

      // ========== APPROVE MULTIPLE KARIGARS ==========
      .addCase(approveMultipleKarigars.pending, (state) => {
        state.loading.bulkApprove = true;
        state.error = null;
      })
      .addCase(approveMultipleKarigars.fulfilled, (state, action) => {
        state.loading.bulkApprove = false;
        // Update status of approved karigars in list
        const data = extractData(action.payload);
        if (data.modifiedCount > 0) {
          // Would need karigarIds from payload to update, or refetch
        }
        state.successMessage = action.payload?.message || "Karigars approved";
      })
      .addCase(approveMultipleKarigars.rejected, (state, action) => {
        state.loading.bulkApprove = false;
        state.error = action.payload?.message || "Failed to approve karigars";
      })

      // ========== SUSPEND MULTIPLE KARIGARS ==========
      .addCase(suspendMultipleKarigars.pending, (state) => {
        state.loading.bulkSuspend = true;
        state.error = null;
      })
      .addCase(suspendMultipleKarigars.fulfilled, (state, action) => {
        state.loading.bulkSuspend = false;
        state.successMessage = action.payload?.message || "Karigars suspended";
      })
      .addCase(suspendMultipleKarigars.rejected, (state, action) => {
        state.loading.bulkSuspend = false;
        state.error = action.payload?.message || "Failed to suspend karigars";
      })

      // ========== DELETE MULTIPLE KARIGARS ==========
      .addCase(deleteMultipleKarigars.pending, (state) => {
        state.loading.bulkDelete = true;
        state.error = null;
      })
      .addCase(deleteMultipleKarigars.fulfilled, (state, action) => {
        state.loading.bulkDelete = false;
        state.successMessage = action.payload?.message || "Karigars deleted";
      })
      .addCase(deleteMultipleKarigars.rejected, (state, action) => {
        state.loading.bulkDelete = false;
        state.error = action.payload?.message || "Failed to delete karigars";
      })

      // ========== SET KARIGAR PASSWORD ==========
      .addCase(setKarigarPassword.pending, (state) => {
        state.loading.password = true;
        state.error = null;
      })
      .addCase(setKarigarPassword.fulfilled, (state, action) => {
        state.loading.password = false;
        state.successMessage =
          action.payload?.message || "Password set successfully";
      })
      .addCase(setKarigarPassword.rejected, (state, action) => {
        state.loading.password = false;
        state.error = action.payload?.message || "Failed to set password";
      })

      // ========== RESET KARIGAR PASSWORD ==========
      .addCase(resetKarigarPassword.pending, (state) => {
        state.loading.password = true;
        state.error = null;
      })
      .addCase(resetKarigarPassword.fulfilled, (state, action) => {
        state.loading.password = false;
        state.successMessage =
          action.payload?.message || "Password reset successfully";
      })
      .addCase(resetKarigarPassword.rejected, (state, action) => {
        state.loading.password = false;
        state.error = action.payload?.message || "Failed to reset password";
      })

      // ========== CHECK KARIGAR PASSWORD ==========
      .addCase(checkKarigarPassword.pending, (state) => {
        state.loading.password = true;
        state.error = null;
      })
      .addCase(checkKarigarPassword.fulfilled, (state, action) => {
        state.loading.password = false;
        const data = extractData(action.payload);
        state.passwordStatus = data;
      })
      .addCase(checkKarigarPassword.rejected, (state, action) => {
        state.loading.password = false;
        state.error =
          action.payload?.message || "Failed to check password status";
      })

      // ========== VERIFY BANK DETAILS ==========
      .addCase(verifyBankDetails.pending, (state) => {
        state.loading.verification = true;
        state.error = null;
      })
      .addCase(verifyBankDetails.fulfilled, (state, action) => {
        state.loading.verification = false;
        state.successMessage =
          action.payload?.message || "Bank details verified";
      })
      .addCase(verifyBankDetails.rejected, (state, action) => {
        state.loading.verification = false;
        state.error =
          action.payload?.message || "Failed to verify bank details";
      })

      // ========== FETCH ESTIMATED HOURS FOR PRODUCT ==========
      .addCase(fetchEstimatedHoursForProduct.pending, (state) => {
        state.loading.estimate = true;
        state.error = null;
      })
      .addCase(fetchEstimatedHoursForProduct.fulfilled, (state, action) => {
        state.loading.estimate = false;
        const data = extractData(action.payload);
        state.estimatedHours = data;
      })
      .addCase(fetchEstimatedHoursForProduct.rejected, (state, action) => {
        state.loading.estimate = false;
        state.error =
          action.payload?.message || "Failed to fetch estimated hours";
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  setFilters,
  resetFilters,
  clearCurrentKarigar,
  clearAvailability,
  clearAvailableKarigars,
  clearPerformanceReport,
  clearBulkAvailability,
  clearEstimatedHours,
  setPagination,
} = karigarSlice.actions;

export default karigarSlice.reducer;
